import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Agent, Integration, Project, Task, TaskOperation, Workspace, WorkspaceMember } from "@shared/domain";
import { useLocation } from "react-router-dom";
import { apiClient, type RemoteAgentConnectionInput, type RemoteAgentUpdateInput } from "@/api/client";
import { extractAnalyticsErrorMeta, trackEvent } from "@/analytics/events";
import { useAppStore } from "@/state/appStore";
import type { ClarificationInput, CreateTaskInput } from "@/state/types";

export const QUERY_KEYS = {
  workspace: ["api", "workspace"] as const,
  goals: ["api", "goals"] as const,
  projects: ["api", "projects"] as const,
  reports: ["api", "reports"] as const,
  agents: ["api", "agents"] as const,
  discoverAgents: ["api", "discover-agents"] as const,
  members: ["api", "members"] as const,
  pendingAssignments: ["api", "pending-assignments"] as const,
  credits: ["api", "credits"] as const,
  creditLedger: ["api", "credit-ledger"] as const,
  notifications: ["api", "notifications"] as const,
  integrations: ["api", "integrations"] as const,
  tasks: ["api", "tasks"] as const,
  subtasks: ["api", "subtasks"] as const,
  atomicTasks: ["api", "atomic-tasks"] as const,
  activity: ["api", "activity"] as const,
  taskOutput: ["api", "task-output"] as const,
  taskSnapshot: (taskId: string) => ["api", "task-snapshot", taskId] as const,
  taskOperation: (taskId: string, operationId: string) => ["api", "task-operation", taskId, operationId] as const
};

const invalidateAllQueries = async (queryClient: ReturnType<typeof useQueryClient>): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workspace });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.goals });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reports });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agents });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.discoverAgents });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.members });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pendingAssignments });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.credits });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.creditLedger });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.integrations });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subtasks });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.atomicTasks });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activity });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskOutput });
};

const invalidateExecutionQueries = async (queryClient: ReturnType<typeof useQueryClient>): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subtasks });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.atomicTasks });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activity });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskOutput });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reports });
};

const scheduleExecutionRefreshBurst = (queryClient: ReturnType<typeof useQueryClient>): void => {
  const delaysMs = [900, 1800, 3200, 5000, 7600];
  for (const delay of delaysMs) {
    setTimeout(() => {
      void invalidateExecutionQueries(queryClient);
    }, delay);
  }
};

const clarityBand = (score: number | undefined): "low" | "medium" | "high" | undefined => {
  if (typeof score !== "number" || Number.isNaN(score)) return undefined;
  if (score <= 0.75) return "low";
  if (score <= 0.9) return "medium";
  return "high";
};

const trackErrorEvent = (eventName: "auth_failed" | "task_plan_generation_failed" | "credits_purchase_failed", error: unknown, payload: Record<string, string | number | boolean | undefined> = {}) => {
  const meta = extractAnalyticsErrorMeta(error);
  trackEvent(eventName, { ...payload, ...meta });
};

const buildRealtimeSocketUrl = (): string => {
  const configured = import.meta.env.VITE_REALTIME_WS_URL;
  if (typeof configured === "string" && configured.trim().length > 0) {
    return configured.trim();
  }
  const apiBase = import.meta.env.VITE_API_BASE_URL || "/api";
  if (/^https?:\/\//i.test(apiBase)) {
    const url = new URL(apiBase);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    const cleanPath = url.pathname.replace(/\/+$/, "");
    const apiPath = cleanPath.endsWith("/api") ? cleanPath : `${cleanPath}/api`;
    url.pathname = `${apiPath}/realtime/activity`;
    url.search = "";
    return url.toString();
  }
  const url = new URL(window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = "/api/realtime/activity";
  return url.toString();
};

export const deriveRealtimeInvalidationDecision = (input: {
  eventType?: string;
  touchesWorkspace: boolean;
  isTodayRoute: boolean;
  isTasksListRoute: boolean;
  isActivityRoute: boolean;
}): {
  queueOnlyEvent: boolean;
  completionEvent: boolean;
  invalidateTasks: boolean;
  invalidateSubtasks: boolean;
  invalidateAtomicTasks: boolean;
  invalidateActivity: boolean;
  invalidateCredits: boolean;
} => {
  const queueOnlyEvent = input.eventType === "atomic.dispatch";
  const completionEvent = input.eventType === "atomic.completed" || input.eventType === "task.completed";
  const isExecutionRoute = input.isTodayRoute || input.isTasksListRoute || input.isActivityRoute;
  const invalidateTasks = input.touchesWorkspace && !queueOnlyEvent;
  const invalidateSubtasks = input.touchesWorkspace && isExecutionRoute && !queueOnlyEvent;
  const invalidateAtomicTasks = input.touchesWorkspace && isExecutionRoute && !queueOnlyEvent;
  const invalidateActivity = input.touchesWorkspace && input.isActivityRoute;
  const invalidateCredits = input.touchesWorkspace && completionEvent;
  return {
    queueOnlyEvent,
    completionEvent,
    invalidateTasks,
    invalidateSubtasks,
    invalidateAtomicTasks,
    invalidateActivity,
    invalidateCredits
  };
};

const pollTaskOperationUntilSettled = async (params: {
  taskId: string;
  operationId: string;
  queryClient: ReturnType<typeof useQueryClient>;
  onUpdate?: (operation?: TaskOperation) => void;
}): Promise<TaskOperation | undefined> => {
  const pollDelaysMs = [800, 1000, 1200, 1500, 1800, 2200, 2800, 3500, 4200, 5000];
  const maxAttempts = 60;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const operation = await apiClient.getTaskOperation(params.taskId, params.operationId);
      params.onUpdate?.(operation);
      void params.queryClient.setQueryData(QUERY_KEYS.taskOperation(params.taskId, params.operationId), operation);
      if (operation.status === "succeeded" || operation.status === "failed") {
        return operation;
      }
    } catch {
      // transient request failures are retried by the polling loop
    }
    const delayMs = pollDelaysMs[Math.min(attempt, pollDelaysMs.length - 1)];
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
  return undefined;
};

export const useBackendSync = (): void => {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const workspaceId = useAppStore((state) => state.workspace.id);
  const setWorkspaceFromApi = useAppStore((state) => state.setWorkspaceFromApi);
  const setGoalsFromApi = useAppStore((state) => state.setGoalsFromApi);
  const setProjectsFromApi = useAppStore((state) => state.setProjectsFromApi);
  const setReportsFromApi = useAppStore((state) => state.setReportsFromApi);
  const setAgentsFromApi = useAppStore((state) => state.setAgentsFromApi);
  const setDiscoverAgentsFromApi = useAppStore((state) => state.setDiscoverAgentsFromApi);
  const setMembersFromApi = useAppStore((state) => state.setMembersFromApi);
  const setPendingAssignmentsFromApi = useAppStore((state) => state.setPendingAssignmentsFromApi);
  const setCreditLedgerFromApi = useAppStore((state) => state.setCreditLedgerFromApi);
  const setNotificationsFromApi = useAppStore((state) => state.setNotificationsFromApi);
  const setIntegrationsFromApi = useAppStore((state) => state.setIntegrationsFromApi);
  const setTasksFromApi = useAppStore((state) => state.setTasksFromApi);
  const setSubtasksFromApi = useAppStore((state) => state.setSubtasksFromApi);
  const setAtomicTasksFromApi = useAppStore((state) => state.setAtomicTasksFromApi);
  const setActivitiesFromApi = useAppStore((state) => state.setActivitiesFromApi);
  const mergeTaskSnapshotFromApi = useAppStore((state) => state.mergeTaskSnapshotFromApi);
  const setTaskOperationForTask = useAppStore((state) => state.setTaskOperationForTask);
  const pushToast = useAppStore((state) => state.pushToast);

  const [tabVisible, setTabVisible] = useState(() => document.visibilityState !== "hidden");
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [socketVersion, setSocketVersion] = useState(0);
  const snapshotInvalidateTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const hasShownDomainError = useRef(false);
  const hasShownExecutionError = useRef(false);
  const hasShownActivityError = useRef(false);
  const hasShownBackendUnavailable = useRef(false);
  const trackedLedgerEntriesRef = useRef<Set<string>>(new Set());
  const previousCreditBalanceRef = useRef<number | undefined>(undefined);

  const taskDetailMatch = pathname.match(/^\/tasks\/([^/]+)$/);
  const taskDetailsId = taskDetailMatch?.[1];
  const isPublicRoute = pathname === "/" || pathname === "/auth";
  const shouldSyncWorkspaceDomain = !isPublicRoute;
  const isTodayRoute = /^\/today(\/|$)/.test(pathname);
  const isTasksListRoute = /^\/tasks\/?$/.test(pathname);
  const isTaskDetailsRoute = Boolean(taskDetailsId);
  const isActivityRoute = /^\/activity(\/|$)/.test(pathname);
  const isReportsRoute = /^\/reports(\/|$)/.test(pathname);
  const isAgentsRoute = /^\/agents(\/|$)/.test(pathname);
  const isSettingsRoute = /^\/settings(\/|$)/.test(pathname);
  const isSettingsTeamRoute = /^\/settings\/team(\/|$)/.test(pathname);
  const isSettingsNotificationsRoute = /^\/settings\/notifications(\/|$)/.test(pathname);
  const isSettingsIntegrationsRoute = /^\/settings\/integrations(\/|$)/.test(pathname);
  const isOnboardingRoute = /^\/onboarding(\/|$)/.test(pathname);

  const shouldSyncWorkspace = shouldSyncWorkspaceDomain;
  const shouldSyncGoals = isTodayRoute || isOnboardingRoute || isReportsRoute || isTasksListRoute;
  const shouldSyncProjects = isTodayRoute || isTasksListRoute || isTaskDetailsRoute || isActivityRoute || isOnboardingRoute || isReportsRoute;
  const shouldSyncReports = isTodayRoute || isReportsRoute;
  const shouldSyncAgents = isAgentsRoute || isTasksListRoute || isTaskDetailsRoute;
  const shouldSyncDiscoverAgents = isAgentsRoute;
  const shouldSyncMembers = isTasksListRoute || isTaskDetailsRoute || isSettingsTeamRoute || isOnboardingRoute;
  const shouldSyncPendingAssignments = isSettingsTeamRoute;
  const shouldSyncCredits = isSettingsRoute || isReportsRoute;
  const shouldSyncCreditLedger = isSettingsRoute || isReportsRoute;
  const shouldSyncNotifications = isSettingsNotificationsRoute;
  const shouldSyncIntegrations = isSettingsIntegrationsRoute;
  const shouldSyncTasks = isTodayRoute || isTasksListRoute || isActivityRoute || isReportsRoute;
  const shouldSyncSubtasks = isTodayRoute || isTasksListRoute || isActivityRoute;
  const shouldSyncAtomicTasks = isTodayRoute || isTasksListRoute || isActivityRoute;
  const shouldSyncActivity = isActivityRoute;
  const shouldSyncTaskSnapshot = isTaskDetailsRoute && Boolean(taskDetailsId);

  const shouldPollExecutionGraph = (isTodayRoute || isTasksListRoute || isActivityRoute) && tabVisible;
  const executionRefetchInterval: number | false = shouldPollExecutionGraph ? (realtimeConnected ? 45_000 : 20_000) : false;
  const activityRefetchInterval: number | false = shouldSyncActivity && tabVisible ? (realtimeConnected ? 30_000 : 12_000) : false;
  const taskSnapshotRefetchInterval: number | false = shouldSyncTaskSnapshot && tabVisible ? (realtimeConnected ? 20_000 : 8_000) : false;

  const scheduleTaskSnapshotInvalidation = useCallback(
    (taskId: string): void => {
      const timers = snapshotInvalidateTimersRef.current;
      const existing = timers.get(taskId);
      if (existing) clearTimeout(existing);
      const timeout = setTimeout(() => {
        timers.delete(taskId);
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskSnapshot(taskId) });
        void queryClient.invalidateQueries({ queryKey: ["api", "task-operation", taskId] });
        void queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.taskOutput, taskId] });
      }, 750);
      timers.set(taskId, timeout);
    },
    [queryClient]
  );

  useEffect(() => {
    const onVisibilityChange = () => setTabVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      for (const timeout of snapshotInvalidateTimersRef.current.values()) {
        clearTimeout(timeout);
      }
      snapshotInvalidateTimersRef.current.clear();
    };
  }, []);

  const healthQuery = useQuery({
    queryKey: ["api", "health"],
    queryFn: apiClient.getHealth,
    enabled: shouldSyncWorkspaceDomain,
    retry: 1,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false
  });

  const backendAvailable = healthQuery.isSuccess;

  const domainQueryOptions = {
    refetchInterval: false as const,
    refetchOnWindowFocus: false,
    staleTime: 30_000
  };

  const executionQueryOptions = {
    refetchInterval: executionRefetchInterval,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    staleTime: executionRefetchInterval ? 0 : 30_000
  };

  const domainEnabled = (enabled: boolean): boolean => shouldSyncWorkspaceDomain && backendAvailable && enabled;
  const executionEnabled = (enabled: boolean): boolean => shouldSyncWorkspaceDomain && backendAvailable && enabled;

  const workspaceQuery = useQuery({
    ...domainQueryOptions,
    enabled: domainEnabled(shouldSyncWorkspace),
    queryKey: QUERY_KEYS.workspace,
    queryFn: async () => {
      const list = await apiClient.getWorkspaces();
      return list[0];
    }
  });
  const goalsQuery = useQuery({ ...domainQueryOptions, enabled: domainEnabled(shouldSyncGoals), queryKey: QUERY_KEYS.goals, queryFn: apiClient.getGoals });
  const projectsQuery = useQuery({
    ...domainQueryOptions,
    enabled: domainEnabled(shouldSyncProjects),
    queryKey: QUERY_KEYS.projects,
    queryFn: apiClient.getProjects
  });
  const reportsQuery = useQuery({
    ...domainQueryOptions,
    enabled: domainEnabled(shouldSyncReports),
    queryKey: QUERY_KEYS.reports,
    queryFn: () => apiClient.getReports("workspace")
  });
  const agentsQuery = useQuery({ ...domainQueryOptions, enabled: domainEnabled(shouldSyncAgents), queryKey: QUERY_KEYS.agents, queryFn: apiClient.getAgents });
  const discoverAgentsQuery = useQuery({
    ...domainQueryOptions,
    enabled: domainEnabled(shouldSyncDiscoverAgents),
    queryKey: QUERY_KEYS.discoverAgents,
    queryFn: apiClient.getDiscoverAgents
  });
  const membersQuery = useQuery({
    ...domainQueryOptions,
    enabled: domainEnabled(shouldSyncMembers),
    queryKey: QUERY_KEYS.members,
    queryFn: apiClient.getMembers
  });
  const pendingAssignmentsQuery = useQuery({
    ...domainQueryOptions,
    enabled: domainEnabled(shouldSyncPendingAssignments),
    queryKey: QUERY_KEYS.pendingAssignments,
    queryFn: apiClient.getPendingAssignments
  });
  const creditsQuery = useQuery({
    ...domainQueryOptions,
    enabled: domainEnabled(shouldSyncCredits),
    queryKey: QUERY_KEYS.credits,
    queryFn: apiClient.getCredits
  });
  const creditLedgerQuery = useQuery({
    ...domainQueryOptions,
    enabled: domainEnabled(shouldSyncCreditLedger),
    queryKey: QUERY_KEYS.creditLedger,
    queryFn: apiClient.getCreditsLedger
  });
  const notificationsQuery = useQuery({
    ...domainQueryOptions,
    enabled: domainEnabled(shouldSyncNotifications),
    queryKey: QUERY_KEYS.notifications,
    queryFn: apiClient.getNotifications
  });
  const integrationsQuery = useQuery({
    ...domainQueryOptions,
    enabled: domainEnabled(shouldSyncIntegrations),
    queryKey: QUERY_KEYS.integrations,
    queryFn: apiClient.getIntegrations
  });

  const tasksQuery = useQuery({
    ...executionQueryOptions,
    enabled: executionEnabled(shouldSyncTasks),
    queryKey: QUERY_KEYS.tasks,
    queryFn: apiClient.getTasks
  });
  const subtasksQuery = useQuery({
    ...executionQueryOptions,
    enabled: executionEnabled(shouldSyncSubtasks),
    queryKey: QUERY_KEYS.subtasks,
    queryFn: () => apiClient.getSubtasks()
  });
  const atomicTasksQuery = useQuery({
    ...executionQueryOptions,
    enabled: executionEnabled(shouldSyncAtomicTasks),
    queryKey: QUERY_KEYS.atomicTasks,
    queryFn: () => apiClient.getAtomicTasks()
  });
  const activityQuery = useQuery({
    enabled: executionEnabled(shouldSyncActivity),
    queryKey: [...QUERY_KEYS.activity, "latest"],
    queryFn: () => apiClient.getActivity({ limit: 80 }),
    refetchOnWindowFocus: false,
    refetchInterval: activityRefetchInterval,
    refetchIntervalInBackground: false,
    staleTime: activityRefetchInterval ? 0 : 30_000
  });
  const taskSnapshotQuery = useQuery({
    enabled: executionEnabled(shouldSyncTaskSnapshot) && Boolean(taskDetailsId),
    queryKey: QUERY_KEYS.taskSnapshot(taskDetailsId ?? "none"),
    queryFn: ({ signal }) => apiClient.getTaskSnapshot(taskDetailsId ?? "", { signal }),
    refetchOnWindowFocus: false,
    refetchInterval: taskSnapshotRefetchInterval,
    refetchIntervalInBackground: false,
    staleTime: taskSnapshotRefetchInterval ? 0 : 30_000
  });

  useEffect(() => {
    if (!shouldSyncWorkspaceDomain) return;
    if (healthQuery.isError && !hasShownBackendUnavailable.current) {
      hasShownBackendUnavailable.current = true;
      pushToast({ kind: "error", message: "API unavailable. Check backend/auth configuration." });
      return;
    }
    if (healthQuery.isSuccess) {
      hasShownBackendUnavailable.current = false;
      hasShownDomainError.current = false;
      hasShownExecutionError.current = false;
      hasShownActivityError.current = false;
    }
  }, [healthQuery.isError, healthQuery.isSuccess, pushToast, shouldSyncWorkspaceDomain]);

  useEffect(() => {
    if (workspaceQuery.data) {
      setWorkspaceFromApi(workspaceQuery.data);
      hasShownDomainError.current = false;
    }
  }, [setWorkspaceFromApi, workspaceQuery.data]);

  useEffect(() => {
    if (goalsQuery.data) {
      setGoalsFromApi(goalsQuery.data);
      hasShownDomainError.current = false;
    }
  }, [goalsQuery.data, setGoalsFromApi]);

  useEffect(() => {
    if (projectsQuery.data) {
      setProjectsFromApi(projectsQuery.data);
      hasShownDomainError.current = false;
    }
  }, [projectsQuery.data, setProjectsFromApi]);

  useEffect(() => {
    if (reportsQuery.data) {
      setReportsFromApi(reportsQuery.data);
      hasShownDomainError.current = false;
    }
  }, [reportsQuery.data, setReportsFromApi]);

  useEffect(() => {
    if (agentsQuery.data) {
      setAgentsFromApi(agentsQuery.data);
      hasShownDomainError.current = false;
    }
  }, [agentsQuery.data, setAgentsFromApi]);

  useEffect(() => {
    if (discoverAgentsQuery.data) {
      setDiscoverAgentsFromApi(discoverAgentsQuery.data);
      hasShownDomainError.current = false;
    }
  }, [discoverAgentsQuery.data, setDiscoverAgentsFromApi]);

  useEffect(() => {
    if (membersQuery.data) {
      setMembersFromApi(membersQuery.data);
      hasShownDomainError.current = false;
    }
  }, [membersQuery.data, setMembersFromApi]);

  useEffect(() => {
    if (pendingAssignmentsQuery.data) {
      setPendingAssignmentsFromApi(pendingAssignmentsQuery.data);
      hasShownDomainError.current = false;
    }
  }, [pendingAssignmentsQuery.data, setPendingAssignmentsFromApi]);

  useEffect(() => {
    if (workspaceQuery.data && creditsQuery.data) {
      setWorkspaceFromApi({ ...workspaceQuery.data, creditBalance: creditsQuery.data.balance });
      hasShownDomainError.current = false;
    }
  }, [creditsQuery.data, setWorkspaceFromApi, workspaceQuery.data]);

  useEffect(() => {
    if (creditLedgerQuery.data) {
      setCreditLedgerFromApi(creditLedgerQuery.data);
      hasShownDomainError.current = false;

      for (const entry of creditLedgerQuery.data) {
        if (trackedLedgerEntriesRef.current.has(entry.id)) continue;
        trackedLedgerEntriesRef.current.add(entry.id);
        if (entry.type !== "debit") continue;
        trackEvent("credit_debit_recorded", {
          operation_type: entry.operationType || "unknown",
          credits_amount: Math.abs(entry.amount),
          workspace_id: entry.workspaceId,
          task_id: entry.taskId
        });
      }
    }
  }, [creditLedgerQuery.data, setCreditLedgerFromApi]);

  useEffect(() => {
    const balance = creditsQuery.data?.balance;
    if (typeof balance !== "number") return;

    const previous = previousCreditBalanceRef.current;
    previousCreditBalanceRef.current = balance;
    if (typeof previous !== "number") return;

    if (previous > 30 && balance <= 30) {
      trackEvent("low_credit_threshold_reached", { threshold: 30, credit_balance_after: balance });
    }
    if (previous > 10 && balance <= 10) {
      trackEvent("low_credit_threshold_reached", { threshold: 10, credit_balance_after: balance });
    }
    if (previous > 0 && balance <= 0) {
      trackEvent("low_credit_threshold_reached", { threshold: 0, credit_balance_after: balance });
    }
  }, [creditsQuery.data?.balance]);

  useEffect(() => {
    if (notificationsQuery.data) {
      setNotificationsFromApi(notificationsQuery.data);
      hasShownDomainError.current = false;
    }
  }, [notificationsQuery.data, setNotificationsFromApi]);

  useEffect(() => {
    if (integrationsQuery.data) {
      setIntegrationsFromApi(integrationsQuery.data);
      hasShownDomainError.current = false;
    }
  }, [integrationsQuery.data, setIntegrationsFromApi]);

  useEffect(() => {
    if (tasksQuery.data) {
      setTasksFromApi(tasksQuery.data);
      hasShownExecutionError.current = false;
    }
  }, [setTasksFromApi, tasksQuery.data]);

  useEffect(() => {
    if (subtasksQuery.data) {
      setSubtasksFromApi(subtasksQuery.data);
      hasShownExecutionError.current = false;
    }
  }, [setSubtasksFromApi, subtasksQuery.data]);

  useEffect(() => {
    if (atomicTasksQuery.data) {
      setAtomicTasksFromApi(atomicTasksQuery.data);
      hasShownExecutionError.current = false;
    }
  }, [atomicTasksQuery.data, setAtomicTasksFromApi]);

  useEffect(() => {
    if (activityQuery.data) {
      setActivitiesFromApi(activityQuery.data.items);
      hasShownActivityError.current = false;
    }
  }, [activityQuery.data, setActivitiesFromApi]);

  useEffect(() => {
    if (!taskSnapshotQuery.data) return;
    mergeTaskSnapshotFromApi(taskSnapshotQuery.data);
    setTaskOperationForTask(taskSnapshotQuery.data.task.id, taskSnapshotQuery.data.activeOperation);
    hasShownExecutionError.current = false;
    hasShownActivityError.current = false;
  }, [mergeTaskSnapshotFromApi, setTaskOperationForTask, taskSnapshotQuery.data]);

  useEffect(() => {
    if (!backendAvailable) return;
    const domainError =
      workspaceQuery.isError ||
      goalsQuery.isError ||
      projectsQuery.isError ||
      reportsQuery.isError ||
      agentsQuery.isError ||
      discoverAgentsQuery.isError ||
      membersQuery.isError ||
      pendingAssignmentsQuery.isError ||
      creditsQuery.isError ||
      creditLedgerQuery.isError ||
      notificationsQuery.isError ||
      integrationsQuery.isError;
    if (!domainError || hasShownDomainError.current) return;
    hasShownDomainError.current = true;
    pushToast({ kind: "error", message: "Workspace data sync failed. Retrying automatically." });
  }, [
    backendAvailable,
    agentsQuery.isError,
    discoverAgentsQuery.isError,
    membersQuery.isError,
    pendingAssignmentsQuery.isError,
    creditsQuery.isError,
    creditLedgerQuery.isError,
    goalsQuery.isError,
    integrationsQuery.isError,
    notificationsQuery.isError,
    projectsQuery.isError,
    pushToast,
    reportsQuery.isError,
    workspaceQuery.isError
  ]);

  useEffect(() => {
    if (!backendAvailable) return;
    const executionError = tasksQuery.isError || subtasksQuery.isError || atomicTasksQuery.isError || (shouldSyncTaskSnapshot && taskSnapshotQuery.isError);
    if (!executionError || hasShownExecutionError.current) return;
    hasShownExecutionError.current = true;
    pushToast({ kind: "error", message: "Execution graph sync failed. Retrying automatically." });
  }, [atomicTasksQuery.isError, backendAvailable, pushToast, shouldSyncTaskSnapshot, subtasksQuery.isError, taskSnapshotQuery.isError, tasksQuery.isError]);

  useEffect(() => {
    if (!backendAvailable) return;
    const activityError = activityQuery.isError || (shouldSyncTaskSnapshot && taskSnapshotQuery.isError);
    if (!activityError || hasShownActivityError.current) return;
    hasShownActivityError.current = true;
    pushToast({ kind: "error", message: "Activity sync failed. Retrying automatically." });
  }, [activityQuery.isError, backendAvailable, pushToast, shouldSyncTaskSnapshot, taskSnapshotQuery.isError]);

  useEffect(() => {
    const shouldUseRealtime = shouldSyncWorkspaceDomain && backendAvailable && (isTodayRoute || isTasksListRoute || isTaskDetailsRoute || isActivityRoute);
    if (!shouldUseRealtime || !workspaceId) {
      setRealtimeConnected(false);
      return;
    }

    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    const url = new URL(buildRealtimeSocketUrl());
    const channels = [`workspace:${workspaceId}`];
    if (taskDetailsId) channels.push(`task:${taskDetailsId}`);
    url.searchParams.set("channels", channels.join(","));

    const socket = new WebSocket(url.toString());

    socket.addEventListener("open", () => {
      if (cancelled) return;
      setRealtimeConnected(true);
    });

    socket.addEventListener("error", () => {
      if (cancelled) return;
      setRealtimeConnected(false);
    });

    socket.addEventListener("close", () => {
      if (cancelled) return;
      setRealtimeConnected(false);
      reconnectTimer = setTimeout(() => {
        setSocketVersion((value) => value + 1);
      }, 2500);
    });

    socket.addEventListener("message", (event) => {
      if (cancelled) return;
      try {
        const message = JSON.parse(String(event.data)) as Record<string, unknown>;
        if (message.type !== "event") return;
        const payload = message.payload && typeof message.payload === "object" ? (message.payload as Record<string, unknown>) : {};
        const wsChannels = Array.isArray(message.channels) ? message.channels.filter((item): item is string => typeof item === "string") : [];
        const eventType = typeof payload.type === "string" ? payload.type : undefined;
        const eventTaskId = typeof payload.taskId === "string" ? payload.taskId : undefined;
        const workspaceChannel = `workspace:${workspaceId}`;
        const touchesWorkspace = wsChannels.includes(workspaceChannel);
        const touchesCurrentTask =
          Boolean(taskDetailsId) && (wsChannels.includes(`task:${taskDetailsId}`) || (typeof eventTaskId === "string" && eventTaskId === taskDetailsId));
        const decision = deriveRealtimeInvalidationDecision({
          eventType,
          touchesWorkspace,
          isTodayRoute,
          isTasksListRoute,
          isActivityRoute
        });

        const taskIdsToRefresh = new Set<string>();
        if (eventTaskId) taskIdsToRefresh.add(eventTaskId);
        if (touchesCurrentTask && taskDetailsId) taskIdsToRefresh.add(taskDetailsId);
        if (!decision.queueOnlyEvent || touchesCurrentTask) {
          for (const taskId of taskIdsToRefresh) {
            scheduleTaskSnapshotInvalidation(taskId);
          }
        }

        if (touchesWorkspace) {
          if (decision.invalidateTasks) {
            void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
          }
          if (decision.invalidateAtomicTasks) {
            void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.atomicTasks });
          }
          if (decision.invalidateSubtasks) {
            void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subtasks });
          }
          if (decision.invalidateActivity) {
            void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activity });
          }
          if (decision.invalidateCredits) {
            void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.credits });
            void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.creditLedger });
            void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workspace });
            void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reports });
          }
        }
      } catch {
        // ignored
      }
    });

    return () => {
      cancelled = true;
      setRealtimeConnected(false);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      socket.close();
    };
  }, [
    backendAvailable,
    isActivityRoute,
    isTaskDetailsRoute,
    isTasksListRoute,
    isTodayRoute,
    queryClient,
    scheduleTaskSnapshotInvalidation,
    shouldSyncWorkspaceDomain,
    socketVersion,
    taskDetailsId,
    workspaceId
  ]);
};

export const useTaskOutputQuery = (taskId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: taskId ? [...QUERY_KEYS.taskOutput, taskId] : [...QUERY_KEYS.taskOutput, "none"],
    queryFn: () => apiClient.getTaskOutput(taskId ?? ""),
    enabled: Boolean(taskId) && enabled,
    staleTime: 20_000,
    refetchOnWindowFocus: false
  });

export const useCreateTaskMutation = () => {
  const queryClient = useQueryClient();
  const upsertTaskFromApi = useAppStore((state) => state.upsertTaskFromApi);
  const setTaskOperationForTask = useAppStore((state) => state.setTaskOperationForTask);
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: (input: CreateTaskInput) => apiClient.createTask(input),
    onSuccess: (result) => {
      const task = result.task;
      const operation = "operation" in result ? result.operation : undefined;

      trackEvent("task_created", {
        workspace_id: task.workspaceId,
        project_id: task.projectId,
        task_id: task.id,
        task_status: task.status,
        assignee_mode: task.assignee.type,
        has_due_at: Boolean(task.dueAt),
        has_attachments: task.attachments.length > 0,
        clarity_band: clarityBand(task.clarityScore)
      });
      if (task.status === "blocked") {
        trackEvent("task_clarification_required", {
          workspace_id: task.workspaceId,
          project_id: task.projectId,
          task_id: task.id,
          task_status: task.status,
          clarity_band: clarityBand(task.clarityScore)
        });
      }

      upsertTaskFromApi(task);
      if (operation) {
        setTaskOperationForTask(task.id, operation);
      }
      const toastMessage =
        task.status === "blocked"
          ? "Task created. Clarification needed to continue."
          : result.deduplicated
            ? "Task created. Existing planning operation is still running."
          : task.status === "defining"
            ? "Task created. AI is generating your execution plan."
            : "Task created and queued for execution.";
      pushToast({
        kind: task.status === "blocked" ? "warning" : "success",
        message: toastMessage
      });
      void invalidateExecutionQueries(queryClient);
      if (task.status !== "blocked") {
        scheduleExecutionRefreshBurst(queryClient);
      }
      if (operation) {
        void pollTaskOperationUntilSettled({
          taskId: task.id,
          operationId: operation.id,
          queryClient,
          onUpdate: (operation) => setTaskOperationForTask(task.id, operation)
        }).then((operation) => {
          if (!operation) return;
          if (operation.status === "failed") {
            pushToast({ kind: "error", message: operation.error || "Task plan generation failed." });
            trackEvent("task_plan_generation_failed", {
              task_id: task.id,
              operation_type: operation.type,
              error_kind: "operation_failed"
            });
          } else if (operation.status === "succeeded") {
            trackEvent("task_plan_generated", {
              task_id: task.id,
              operation_type: operation.type
            });
          }
          void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskSnapshot(task.id) });
          void invalidateExecutionQueries(queryClient);
        });
      }
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Could not create task. Please retry.";
      pushToast({ kind: "error", message });
      trackErrorEvent("task_plan_generation_failed", error, { operation_type: "generate_subtasks" });
    }
  });
};

export const useClarifyTaskMutation = () => {
  const queryClient = useQueryClient();
  const setTaskOperationForTask = useAppStore((state) => state.setTaskOperationForTask);
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: ({ taskId, payload }: { taskId: string; payload: ClarificationInput }) => apiClient.clarifyTask(taskId, payload),
    onSuccess: ({ taskId, operationId, deduplicated }) => {
      trackEvent("clarification_submitted", {
        task_id: taskId
      });
      pushToast({ kind: "success", message: deduplicated ? "Clarification captured. Existing plan generation is still running." : "Clarification captured. Plan generation started." });
      void pollTaskOperationUntilSettled({
        taskId,
        operationId,
        queryClient,
        onUpdate: (operation) => setTaskOperationForTask(taskId, operation)
      }).then((operation) => {
        if (operation?.status === "failed") {
          pushToast({ kind: "error", message: operation.error || "Clarification plan generation failed." });
          trackEvent("task_plan_generation_failed", {
            task_id: taskId,
            operation_type: operation.type,
            error_kind: "operation_failed"
          });
        } else if (operation?.status === "succeeded") {
          trackEvent("task_plan_generated", {
            task_id: taskId,
            operation_type: operation.type
          });
        }
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskSnapshot(taskId) });
        void invalidateExecutionQueries(queryClient);
      });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Could not submit clarification.";
      pushToast({ kind: "error", message });
      trackErrorEvent("task_plan_generation_failed", error, { operation_type: "clarify_plan" });
    }
  });
};

export const useResolveBlockerMutation = () => {
  const queryClient = useQueryClient();
  const setTaskOperationForTask = useAppStore((state) => state.setTaskOperationForTask);
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: (taskId: string) => apiClient.resolveBlocker(taskId),
    onSuccess: (result) => {
      trackEvent("blocker_resolve_requested", {
        task_id: result.taskId
      });
      pushToast({ kind: "success", message: result.deduplicated ? "Blocker resolve received. Existing plan generation is still running." : "Blocker resolved. Re-planning queued." });
      void pollTaskOperationUntilSettled({
        taskId: result.taskId,
        operationId: result.operationId,
        queryClient,
        onUpdate: (operation) => setTaskOperationForTask(result.taskId, operation)
      }).then((operation) => {
        if (operation?.status === "failed") {
          pushToast({ kind: "error", message: operation.error || "Could not resume execution." });
          trackEvent("task_plan_generation_failed", {
            task_id: result.taskId,
            operation_type: operation.type,
            error_kind: "operation_failed"
          });
        } else if (operation?.status === "succeeded") {
          trackEvent("task_plan_generated", {
            task_id: result.taskId,
            operation_type: operation.type
          });
        }
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskSnapshot(result.taskId) });
        void invalidateExecutionQueries(queryClient);
      });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Could not resolve blocker. Please retry.";
      pushToast({ kind: "error", message });
      trackErrorEvent("task_plan_generation_failed", error, { operation_type: "resolve_blocker" });
    }
  });
};

export const useGenerateSubtasksMutation = () => {
  const queryClient = useQueryClient();
  const setTaskOperationForTask = useAppStore((state) => state.setTaskOperationForTask);
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    onMutate: async (taskId: string) => {
      const state = useAppStore.getState();
      const previousOperation = state.taskOperations[taskId];
      const now = new Date().toISOString();
      setTaskOperationForTask(taskId, {
        id: `optimistic_generate_${taskId}_${Date.now()}`,
        workspaceId: state.workspace.id,
        taskId,
        type: "generate_subtasks",
        status: "queued",
        createdAt: now,
        updatedAt: now
      });
      return { taskId, previousOperation };
    },
    mutationFn: (taskId: string) => apiClient.generateSubtasks(taskId),
    onSuccess: (result) => {
      trackEvent("subtasks_regenerated", {
        task_id: result.taskId
      });
      pushToast({ kind: "info", message: result.deduplicated ? "Subtask generation already in progress." : "Subtask generation queued." });
      const state = useAppStore.getState();
      const now = new Date().toISOString();
      setTaskOperationForTask(result.taskId, {
        id: result.operationId,
        workspaceId: state.workspace.id,
        taskId: result.taskId,
        type: "generate_subtasks",
        status: result.status,
        createdAt: now,
        updatedAt: now
      });
      void pollTaskOperationUntilSettled({
        taskId: result.taskId,
        operationId: result.operationId,
        queryClient,
        onUpdate: (operation) => setTaskOperationForTask(result.taskId, operation)
      }).then((operation) => {
        if (operation?.status === "failed") {
          pushToast({ kind: "error", message: operation.error || "Subtask generation failed." });
          trackEvent("task_plan_generation_failed", {
            task_id: result.taskId,
            operation_type: operation.type,
            error_kind: "operation_failed"
          });
        } else if (operation?.status === "succeeded") {
          trackEvent("task_plan_generated", {
            task_id: result.taskId,
            operation_type: operation.type
          });
        }
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskSnapshot(result.taskId) });
        void invalidateExecutionQueries(queryClient);
      });
    },
    onError: (error, _taskId, context) => {
      if (context?.taskId) {
        setTaskOperationForTask(context.taskId, context.previousOperation);
      }
      const message = error instanceof Error ? error.message : "Could not queue subtask generation.";
      pushToast({ kind: "error", message });
      trackErrorEvent("task_plan_generation_failed", error, { operation_type: "generate_subtasks" });
    }
  });
};

export const useCompleteAtomicTaskMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: (atomicTaskId: string) => apiClient.completeAtomicTask(atomicTaskId),
    onSuccess: async (result) => {
      trackEvent("atomic_task_completed", {
        atomic_task_id: result.atomicTask.id,
        task_id: result.atomicTask.taskId,
        task_status: result.taskStatus,
        activated_count: result.activated
      });
      if (result.taskStatus === "done") {
        trackEvent("task_completed", {
          task_id: result.atomicTask.taskId,
          task_status: result.taskStatus
        });
      }
      pushToast({
        kind: result.taskStatus === "done" ? "success" : "info",
        message: result.taskStatus === "done" ? "Task complete." : "Atomic step completed."
      });
      await invalidateExecutionQueries(queryClient);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Could not complete atomic task.";
      pushToast({ kind: "error", message });
    }
  });
};

export const useRetryAtomicTaskMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: (atomicTaskId: string) => apiClient.retryAtomicTask(atomicTaskId),
    onSuccess: async () => {
      pushToast({ kind: "info", message: "Agent step queued for retry." });
      await invalidateExecutionQueries(queryClient);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Could not retry agent step.";
      pushToast({ kind: "error", message });
    }
  });
};

export const useCreateSubtaskMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: (input: {
      taskId: string;
      body: { title: string; assignee?: Task["assignee"]; status?: Task["status"]; dependsOn?: string[] };
    }) => apiClient.createSubtask(input.taskId, input.body),
    onSuccess: async () => {
      pushToast({ kind: "success", message: "Subtask added." });
      await invalidateExecutionQueries(queryClient);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Could not add subtask.";
      pushToast({ kind: "error", message });
    }
  });
};

export const useUpdateSubtaskMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: (input: {
      subtaskId: string;
      body: { title?: string; assignee?: Task["assignee"]; status?: Task["status"]; dependsOn?: string[] };
    }) => apiClient.patchSubtask(input.subtaskId, input.body),
    onSuccess: async () => {
      pushToast({ kind: "success", message: "Subtask updated." });
      await invalidateExecutionQueries(queryClient);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Could not update subtask.";
      pushToast({ kind: "error", message });
    }
  });
};

export const useCreateAtomicTaskMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: (input: {
      subtaskId: string;
      body: { title: string; assignee?: Task["assignee"]; status?: Task["status"]; dependsOn?: string[] };
    }) => apiClient.createAtomicTask(input.subtaskId, input.body),
    onSuccess: async () => {
      pushToast({ kind: "success", message: "Atomic task added." });
      await invalidateExecutionQueries(queryClient);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Could not add atomic task.";
      pushToast({ kind: "error", message });
    }
  });
};

export const useUpdateAtomicTaskMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: (input: {
      atomicTaskId: string;
      body: { title?: string; assignee?: Task["assignee"]; status?: Task["status"]; dependsOn?: string[]; lastError?: string };
    }) => apiClient.patchAtomicTask(input.atomicTaskId, input.body),
    onSuccess: async (result) => {
      trackEvent("task_updated", {
        task_id: result.taskId
      });
      pushToast({ kind: "success", message: "Atomic task updated." });
      await invalidateExecutionQueries(queryClient);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Could not update atomic task.";
      pushToast({ kind: "error", message });
    }
  });
};

export const useSwitchWorkspaceMutation = () => {
  const queryClient = useQueryClient();
  const setWorkspaceFromApi = useAppStore((state) => state.setWorkspaceFromApi);
  const setAuthUser = useAppStore((state) => state.setAuthUser);
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: (workspaceId: string) => apiClient.switchWorkspace(workspaceId),
    onSuccess: async ({ workspace, session }) => {
      setWorkspaceFromApi(workspace);
      setAuthUser({
        id: session.user.id,
        name: session.user.displayName,
        email: session.user.email,
        platformRole: session.user.platformRole
      });
      pushToast({ kind: "success", message: `Switched to ${workspace.name}.` });
      trackEvent("workspace_switched", {
        workspace_id: workspace.id
      });
      await invalidateAllQueries(queryClient);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Could not switch workspace.";
      pushToast({ kind: "error", message });
    }
  });
};

export const useUpdateWorkspaceMutation = () => {
  const queryClient = useQueryClient();
  const setWorkspaceFromApi = useAppStore((state) => state.setWorkspaceFromApi);
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Pick<Workspace, "name" | "slug" | "industry" | "teamSize" | "themePreference">> }) =>
      apiClient.patchWorkspace(id, patch),
    onSuccess: async (workspace) => {
      setWorkspaceFromApi(workspace);
      pushToast({ kind: "success", message: "Workspace settings saved." });
      await invalidateAllQueries(queryClient);
    },
    onError: (error) => {
      const message = error instanceof Error && error.message.length > 0 ? error.message : "Could not update workspace settings.";
      pushToast({ kind: "error", message });
    }
  });
};

export const useUpdateAiModelMutation = () => {
  const queryClient = useQueryClient();
  const setWorkspaceFromApi = useAppStore((state) => state.setWorkspaceFromApi);
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: async (model: string) => {
      const result = await apiClient.updateAiModel(model);
      return result.model;
    },
    onSuccess: async (model) => {
      const workspace = await queryClient.fetchQuery({
        queryKey: QUERY_KEYS.workspace,
        queryFn: async () => {
          const list = await apiClient.getWorkspaces();
          return list[0];
        }
      });
      setWorkspaceFromApi({ ...workspace, aiModel: model });
      pushToast({ kind: "success", message: "AI model updated." });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workspace });
    },
    onError: (error) => {
      const message = error instanceof Error && error.message.length > 0 ? error.message : "Could not update AI model.";
      pushToast({ kind: "error", message });
    }
  });
};

export const useCreateGoalMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);
  return useMutation({
    mutationFn: apiClient.createGoal,
    onSuccess: async (goal) => {
      trackEvent("goal_created", {
        workspace_id: goal.workspaceId
      });
      pushToast({ kind: "success", message: "Goal created." });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.goals });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reports });
    },
    onError: () => {
      pushToast({ kind: "error", message: "Could not create goal." });
    }
  });
};

export const useCreateProjectMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);
  return useMutation({
    mutationFn: apiClient.createProject,
    onSuccess: async (project) => {
      trackEvent("project_created", {
        workspace_id: project.workspaceId,
        project_id: project.id
      });
      pushToast({ kind: "success", message: "Project created." });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reports });
    },
    onError: () => {
      pushToast({ kind: "error", message: "Could not create project." });
    }
  });
};

export const useUpdateTaskMutation = () => {
  const queryClient = useQueryClient();
  const upsertTaskFromApi = useAppStore((state) => state.upsertTaskFromApi);
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: async ({
      id,
      patch
    }: {
      id: string;
      patch: Partial<
        Pick<Task, "title" | "description" | "dueAt" | "assignee" | "projectId" | "attachments" | "autoApproveRemoteAgentActions"> & {
          definitionOfDone: string;
        }
      >;
    }) => {
      return apiClient.patchTask(id, patch as Partial<Task>);
    },
    onSuccess: async (result) => {
      upsertTaskFromApi(result);
      pushToast({ kind: "success", message: "Task details updated." });
      trackEvent("task_updated", {
        task_id: result.id,
        project_id: result.projectId
      });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    },
    onError: (error) => {
      const message = error instanceof Error && error.message.length > 0 ? error.message : "Could not update task.";
      pushToast({ kind: "error", message });
    }
  });
};

export const useUpdateProjectMutation = () => {
  const queryClient = useQueryClient();
  const upsertProjectFromApi = useAppStore((state) => state.upsertProjectFromApi);
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: async ({
      id,
      patch
    }: {
      id: string;
      patch: Partial<
        Pick<
          Project,
          "title" | "description" | "color" | "icon" | "defaultAgentId" | "goalIds" | "primaryGoalId" | "memberIds" | "connectedToolIds" | "recommendedAgentIds" | "siteDispatchPolicy"
        >
      >;
    }) => {
      return apiClient.patchProject(id, patch);
    },
    onSuccess: async (result) => {
      upsertProjectFromApi(result);
      pushToast({ kind: "success", message: "Project profile updated." });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
    },
    onError: (error) => {
      const message = error instanceof Error && error.message.length > 0 ? error.message : "Could not update project.";
      pushToast({ kind: "error", message });
    }
  });
};

export const useHireAgentMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);
  return useMutation({
    mutationFn: (catalogId: string) => apiClient.hireAgentFromCatalog(catalogId),
    onSuccess: async (agent: Agent) => {
      pushToast({ kind: "success", message: `${agent.name} added to your workspace.` });
      trackEvent("agent_added", {
        workspace_id: agent.workspaceId
      });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agents });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.discoverAgents });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reports });
    },
    onError: () => {
      pushToast({ kind: "error", message: "Could not add agent." });
    }
  });
};

export const useTestRemoteAgentMutation = () =>
  useMutation({
    mutationFn: (payload: RemoteAgentConnectionInput) => apiClient.testRemoteAgent(payload)
  });

export const useCreateRemoteAgentMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);
  return useMutation({
    mutationFn: (payload: RemoteAgentConnectionInput & { name: string }) => apiClient.createRemoteAgent(payload),
    onSuccess: async (agent) => {
      pushToast({ kind: "success", message: `${agent.name} connected.` });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agents });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reports });
    },
    onError: (error) => {
      pushToast({ kind: "error", message: error instanceof Error ? error.message : "Could not connect remote agent." });
    }
  });
};

export const useUpdateRemoteAgentMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);
  return useMutation({
    mutationFn: ({ agentId, patch }: { agentId: string; patch: RemoteAgentUpdateInput }) =>
      apiClient.updateRemoteAgent(agentId, patch),
    onSuccess: async (agent) => {
      pushToast({ kind: "success", message: `${agent.name} connection updated.` });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agents });
    },
    onError: (error) => {
      pushToast({ kind: "error", message: error instanceof Error ? error.message : "Could not update remote agent." });
    }
  });
};

export const useConnectAgentMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);
  return useMutation({
    mutationFn: ({ agentId, mode }: { agentId: string; mode: "connect" | "disconnect" }) => apiClient.connectAgent(agentId, mode),
    onSuccess: async (result, variables) => {
      pushToast({ kind: "success", message: result.connected ? "Agent connected." : "Agent disconnected." });
      trackEvent("agent_connection_changed", {
        operation_type: variables.mode,
        task_status: result.status
      });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agents });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reports });
    },
    onError: () => {
      pushToast({ kind: "error", message: "Could not update agent connection." });
    }
  });
};

export const useRemoteAgentApprovalMutation = (taskId?: string) => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);
  return useMutation({
    mutationFn: ({ runId, choice }: { runId: string; choice: "once" | "deny" }) =>
      apiClient.submitRemoteAgentApproval(runId, choice),
    onSuccess: async (result) => {
      pushToast({
        kind: result.choice === "once" ? "success" : "warning",
        message: result.choice === "once" ? "Approved once. Remote execution resumed." : "Remote action denied."
      });
      if (taskId) await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskSnapshot(taskId) });
      await invalidateExecutionQueries(queryClient);
    },
    onError: (error) => {
      pushToast({ kind: "error", message: error instanceof Error ? error.message : "Could not submit approval." });
    }
  });
};

export const useInviteMemberMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: (payload: { name: string; email: string; role: WorkspaceMember["role"]; invitedByUserId?: string }) =>
      apiClient.inviteMember(payload),
    onSuccess: async ({ member }) => {
      pushToast({ kind: "success", message: `Invite sent to ${member.email}.` });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.members });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pendingAssignments });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reports });
    },
    onError: () => {
      pushToast({ kind: "error", message: "Could not send invite." });
    }
  });
};

export const usePurchaseCreditsMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: ({ credits, priceLabel }: { credits: number; priceLabel?: string }) => apiClient.purchaseCredits(credits, priceLabel),
    onSuccess: async ({ balance, entry }) => {
      trackEvent("credits_purchase_succeeded", {
        credits_amount: Math.abs(entry.amount),
        credit_balance_after: balance,
        ledger_entry_id: entry.id,
        credit_pack: entry.description
      });
      pushToast({ kind: "success", message: `Purchased ${Math.abs(entry.amount)} credits. Balance: ${balance}.` });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workspace });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.credits });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.creditLedger });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reports });
    },
    onError: (error, variables) => {
      trackErrorEvent("credits_purchase_failed", error, {
        credits_amount: variables.credits,
        credit_pack: variables.priceLabel
      });
      pushToast({ kind: "error", message: "Could not purchase credits." });
    }
  });
};

export const usePatchNotificationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => apiClient.patchNotification(id, enabled),
    onSuccess: async () => {
      trackEvent("notification_toggled", {
        operation_type: "toggle"
      });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications });
    }
  });
};

export const usePatchIntegrationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, detail }: { id: string; status: Integration["status"]; detail?: string }) =>
      apiClient.patchIntegration(id, status, detail),
    onSuccess: async (result) => {
      trackEvent("integration_toggled", {
        operation_type: result.status
      });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.integrations });
    }
  });
};

export const useVerifyWordpressIntegrationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { siteId?: string; siteUrl?: string; customerId?: string }) => apiClient.verifyWordpressIntegration(body),
    onSuccess: async () => {
      trackEvent("integration_toggled", {
        operation_type: "verify_wordpress"
      });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.integrations });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
    }
  });
};

export const useDisconnectWordpressIntegrationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.disconnectWordpressIntegration(),
    onSuccess: async () => {
      trackEvent("integration_toggled", {
        operation_type: "disconnect_wordpress"
      });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.integrations });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
    }
  });
};

export const useArchiveTaskMutation = () => {
  const queryClient = useQueryClient();
  const setTasksFromApi = useAppStore((state) => state.setTasksFromApi);
  const setSubtasksFromApi = useAppStore((state) => state.setSubtasksFromApi);
  const setAtomicTasksFromApi = useAppStore((state) => state.setAtomicTasksFromApi);
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: (taskId: string) => apiClient.archiveTask(taskId),
    onMutate: async (taskId: string) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.tasks });
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.subtasks });
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.atomicTasks });

      const snapshot = useAppStore.getState();
      const previousTasks = snapshot.tasks;
      const previousSubtasks = snapshot.subtasks;
      const previousAtomicTasks = snapshot.atomicTasks;
      const archivedAt = new Date().toISOString();

      setTasksFromApi(previousTasks.map((task) => (task.id === taskId ? { ...task, archivedAt, updatedAt: archivedAt } : task)));
      setSubtasksFromApi(previousSubtasks.map((subtask) => (subtask.taskId === taskId ? { ...subtask, archivedAt } : subtask)));
      setAtomicTasksFromApi(previousAtomicTasks.map((atomic) => (atomic.taskId === taskId ? { ...atomic, archivedAt } : atomic)));

      return { previousTasks, previousSubtasks, previousAtomicTasks };
    },
    onSuccess: async () => {
      pushToast({ kind: "info", message: "Task archived." });
      await invalidateExecutionQueries(queryClient);
    },
    onError: (error, _taskId, context) => {
      if (context) {
        setTasksFromApi(context.previousTasks);
        setSubtasksFromApi(context.previousSubtasks);
        setAtomicTasksFromApi(context.previousAtomicTasks);
      }
      const message = error instanceof Error ? error.message : "Could not archive task.";
      pushToast({ kind: "error", message });
    }
  });
};

export const useArchiveEntityMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: async ({ entityType, id }: { entityType: "goal" | "project" | "task"; id: string }) => apiClient.archiveEntity(entityType, id),
    onSuccess: async () => {
      pushToast({ kind: "info", message: "Item archived." });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.goals });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activity });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Could not archive item.";
      pushToast({ kind: "error", message });
    }
  });
};

export const useRestoreTaskMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: (taskId: string) => apiClient.restoreTask(taskId),
    onSuccess: async () => {
      pushToast({ kind: "success", message: "Task restored." });
      await invalidateExecutionQueries(queryClient);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Could not restore task.";
      pushToast({ kind: "error", message });
    }
  });
};

export const useRestoreEntityMutation = () => {
  const queryClient = useQueryClient();
  const pushToast = useAppStore((state) => state.pushToast);

  return useMutation({
    mutationFn: async ({ entityType, id }: { entityType: "goal" | "project" | "task"; id: string }) => apiClient.restoreEntity(entityType, id),
    onSuccess: async (_result, variables) => {
      pushToast({ kind: "success", message: "Item restored." });
      trackEvent("archive_item_restored", {
        operation_type: variables.entityType,
        task_id: variables.entityType === "task" ? variables.id : undefined,
        project_id: variables.entityType === "project" ? variables.id : undefined
      });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.goals });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activity });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Could not restore item.";
      pushToast({ kind: "error", message });
    }
  });
};
